import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from 'styled-components';
import {
  Content,
  Icon,
  Inline,
  InlineItem,
  Stack,
  Text,
} from '@kinesis/bungle';
import type { InventoryEmissions, SourceType } from '@/types';
import { renderUnit, renderValue } from '@/data/units';
import { sourceColors, tint } from '@/data/colors';
import { renderSourceType, sourceTypes } from '@/data/labels';
import { renderVisualisationTooltip } from '@/components/visualisation-tooltip';
import { LegendContainer } from './inventory-emissions-chart.styles';

// The font stack the app's charts share (annual-emissions,
// emissions-by-sector-source).
// eslint-disable-next-line max-len
const CHART_FONT_FAMILY = `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// The promoted Inventory home page's one chart: annual emissions stacked by
// source type, so a single view carries both trend and composition.
//
// The chart chrome — plot border/background, `marginLeft`, spacing,
// theme-driven axis lines/ticks/gridlines, `useHTML` axis labels (with
// `endOnTick: false` so the top label isn't clipped at the plot edge), the
// zero baseline `plotLines` guard, `noData`, the shared tooltip, and a bespoke
// legend beside the plot (Highcharts' built-in legend disabled; circle-`Icon`
// dot swatches as in `emissions-reductions-legend`) — is the app's standard
// chart config, ported from `annual-emissions` / `emissions-by-sector-source`.
// Only the column-stacking, the source-type series, and the provisional-tail
// treatment are specific to this chart.
//
// The caller passes the whole `InventoryEmissions` payload. `unit` + the
// per-year `total`/`by_source` values are already resolved + scaled server
// side; this component reshapes nothing but the stacking. It assumes at least
// one year — the page renders its own empty state for an inventory with no
// activity (pages own data states, components render what they're given).
const InventoryEmissionsChart = (props: InventoryEmissionsChartProps) => {
  const theme = useTheme() as any;
  const { emissions, height = 360 } = props;
  const { unit, endMonth, years } = emissions;

  // Source types actually present, in the canonical `sourceTypes` order so the
  // stack, colours, and legend are stable across columns. Shared by the series
  // and the legend.
  const presentSourceTypes = useMemo(
    () =>
      sourceTypes.filter((sourceType: SourceType) =>
        years.some((y) => y.bySource.some((s) => s.sourceType === sourceType)),
      ),
    [years],
  );

  const options = useMemo(() => {
    const categories = years.map((y) => `${y.year}`);
    const monthName = MONTHS[endMonth - 1] ?? '';
    const completeByYear = new Map(years.map((y) => [`${y.year}`, y.complete]));

    const series = presentSourceTypes.map((sourceType) => {
      const base = sourceColors[sourceType];
      return {
        name: renderSourceType(sourceType),
        color: base,
        data: years.map((y) => {
          const segment = y.bySource.find((s) => s.sourceType === sourceType);
          if (!segment) return null;
          // A provisional (incomplete) year is lightened so the tail reads as
          // partial without being mistaken for a complete column.
          if (!y.complete) {
            return {
              y: segment.value,
              color: tint(base, 0.45),
              borderColor: base,
            };
          }
          return segment.value;
        }),
      };
    });

    return {
      chart: {
        type: 'column',
        height,
        marginLeft: 120,
        animation: false,
        plotBorderWidth: 1,
        plotBorderColor: theme.colors.gray4.hex,
        plotBackgroundColor: 'rgba(0,0,0,0)',
        spacing: [1, 1, 0, 1],
        style: { fontFamily: CHART_FONT_FAMILY, fontSize: '12px' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      // Built-in legend off; a bespoke legend renders beside the plot (the
      // standard-chart convention).
      legend: { enabled: false },
      lang: { noData: 'There is no data.' },
      noData: {
        style: {
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '20px',
          color: 'rgba(0,0,0,0.25)',
        },
      },
      plotOptions: {
        series: {
          animation: false,
          stacking: 'normal',
          states: { inactive: { opacity: 1 } },
        },
        column: {
          borderWidth: 1,
          borderColor: '#ffffff',
          maxPointWidth: 96,
          states: { hover: { enabled: false } },
        },
      },
      xAxis: {
        crosshair: { color: theme.colors.gray3?.hex, zIndex: -1 },
        categories,
        gridLineWidth: 0,
        lineColor: theme.colors.gray5.hex,
        lineWidth: 1,
        tickColor: theme.colors.gray5.hex,
        tickWidth: 1,
        tickLength: 9,
        title: { enabled: false },
        labels: {
          useHTML: true,
          rotation: 0,
          style: {
            color: 'rgba(0,0,0,0.65)',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '20px',
          },
        },
      },
      yAxis: {
        min: 0.0,
        endOnTick: false,
        // Redraw the zero gridline above the bars so near-zero column borders
        // don't obscure the baseline (the standard-chart guard).
        plotLines: [
          { color: theme.colors.gray4.hex, width: 1, value: 0, zIndex: 20 },
        ],
        gridLineWidth: 1,
        gridLineColor: theme.colors.gray4.hex,
        lineColor: theme.colors.gray5.hex,
        lineWidth: 1,
        labels: {
          x: -9,
          useHTML: true,
          formatter(o: any) {
            return renderValue(o.value, unit);
          },
          style: {
            color: 'rgba(0,0,0,0.65)',
            fontSize: '12px',
            lineHeight: '20px',
            fontWeight: 400,
          },
        },
        title: {
          useHTML: true,
          text: `Emissions (${renderUnit(unit)})`,
          style: {
            color: 'rgba(0,0,0,0.85)',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '20px',
          },
          offset: 106,
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(255,255,255,0)',
        borderWidth: 0,
        shadow: false,
        animation: false,
        hideDelay: 0,
        outside: true,
        padding: 0,
        formatter(): any {
          const o = this as any;
          const year = `${o.x}`;
          const provisional = completeByYear.get(year) === false;
          const title = `Year ending ${monthName} ${year}${
            provisional ? ' · provisional' : ''
          }`;
          return renderVisualisationTooltip({
            title,
            entries: o.points.map((point: any) => ({
              color: point.series.color,
              label: point.series.name,
              value: point.y,
              unit,
            })),
          });
        },
      },
      series,
    };
  }, [theme, years, endMonth, unit, height, presentSourceTypes]);

  // Chart + bespoke legend (the standard circle-`Icon` dots), wrapped in the
  // standard card. `immutable`: recreate on options change rather than mutate
  // in place — the defensive choice the other charts make against Highcharts'
  // incremental-update path, a no-op cost with animation off + memoised
  // options.
  return (
    <Content
      background='white'
      padding='medium'
      border
      paddingMode='equal'
      borderRadiusSize='small'
    >
      <Inline alignY='top'>
        <InlineItem sizing='fill-container'>
          <Content>
            <HighchartsReact
              highcharts={Highcharts}
              options={options}
              immutable
            />
          </Content>
        </InlineItem>
        <InlineItem sizing='fit-content'>
          <LegendContainer>
            <Stack space='xsmall'>
              {presentSourceTypes.map((sourceType) => (
                <Inline key={sourceType} space='small'>
                  <InlineItem>
                    <Icon
                      color={sourceColors[sourceType]}
                      magnitude='small'
                      type='circle'
                    />
                  </InlineItem>
                  <InlineItem>
                    <Text>{renderSourceType(sourceType)}</Text>
                  </InlineItem>
                </Inline>
              ))}
            </Stack>
          </LegendContainer>
        </InlineItem>
      </Inline>
    </Content>
  );
};

export type InventoryEmissionsChartProps = {
  emissions: InventoryEmissions;
  height?: number;
};

export { InventoryEmissionsChart };
